import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { TOKEN_KEY, api, formatApiError } from "@/lib/api";

export const NotificationEmail = ({ current, from }) => {
  const [email, setEmail] = useState(current);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/notification-email", { email });
      setEmail(data.booking_email);
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      qc.invalidateQueries({ queryKey: ["content"] });
      toast.success(`Notifications now go to ${data.booking_email}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form className="ad-card" onSubmit={save} data-testid="settings-notification-email">
      <h3>Where you receive notifications</h3>
      <p className="muted-sm">New booking, shop order, contact-form and review alerts are sent here, and clients' replies to any email land here too. Emails are sent as <strong>{from}</strong>.</p>
      <div className="st-inline">
        <label className="mu-field">Notification email<input type="email" required value={email} onChange={(e) => setEmail(e.target.value.trim())} data-testid="notification-email-input" /></label>
        <button className="btn gold" type="submit" disabled={busy || email === current} data-testid="notification-email-save">{busy ? <Loader2 size={15} className="spin" /> : "Save"}</button>
      </div>
    </form>
  );
};

export const MyAccount = ({ me }) => {
  const [f, setF] = useState({ email: me.email, new_password: "", current_password: "" });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { current_password: f.current_password, email: f.email.trim().toLowerCase() };
      if (f.new_password) body.new_password = f.new_password;
      const { data } = await api.patch("/auth/me", body);
      localStorage.setItem(TOKEN_KEY, data.token);
      qc.setQueryData(["me"], data.user);
      qc.invalidateQueries({ queryKey: ["admin", "staff"] });
      setF({ email: data.user.email, new_password: "", current_password: "" });
      toast.success(body.new_password ? "Login email and password updated" : "Login email updated");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const changed = f.email.trim().toLowerCase() !== me.email || !!f.new_password;
  return (
    <form className="ad-card" onSubmit={save} data-testid="settings-my-account">
      <h3>My login</h3>
      <p className="muted-sm">Change the email you use to sign in to this admin panel, or set a new password. Your current password is required to confirm.</p>
      <div className="shop-form-grid">
        <label className="mu-field">Login email<input type="email" required value={f.email} onChange={set("email")} data-testid="account-email-input" /></label>
        <label className="mu-field">New password (leave blank to keep)<input type="password" minLength={8} maxLength={128} autoComplete="new-password" value={f.new_password} onChange={set("new_password")} data-testid="account-new-password-input" /></label>
        <label className="mu-field">Current password<input type="password" required autoComplete="current-password" value={f.current_password} onChange={set("current_password")} data-testid="account-current-password-input" /></label>
      </div>
      <button className="btn gold" type="submit" disabled={busy || !changed} data-testid="account-save-button">{busy ? <Loader2 size={15} className="spin" /> : "Update my login"}</button>
    </form>
  );
};
